import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getApiUrl } from '../config/apiConfig';
import { LoadingSpinner } from '../components/LoadingSpinner';

interface UrlShortenerQuery {
  url: string;
}

interface UrlShortenerResponse {
  originalUrl: string;
  shortUrl: string;
  shortCode: string;
  createdAt: string;
}

export const UrlShortenerPage: React.FC = () => {
  const [url, setUrl] = useState('');
  const [shortenedUrl, setShortenedUrl] = useState<UrlShortenerResponse | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();

  const handleClear = () => {
    setUrl('');
    setShortenedUrl(null);
    setError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!url) {
      setError('Please enter a URL');
      return;
    }

    setIsLoading(true);
    setError(null);
    
    try {
      const apiUrl = getApiUrl('URL_SHORTENER');
      const requestBody: UrlShortenerQuery = { url };
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.title || 'Failed to shorten URL');
      }
      
      const data = await response.json();
      setShortenedUrl(data);
    } catch (err) {
      console.error('Error shortening URL:', err);
      setError(err instanceof Error ? err.message : 'Failed to shorten URL. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = () => {
    if (shortenedUrl) {
      navigator.clipboard.writeText(shortenedUrl.shortUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Banner */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-6 shadow-md">
        <div className="container mx-auto px-4">
          <button 
            className="mb-3 px-3 py-1 bg-white/20 rounded hover:bg-white/40 text-sm transition-all duration-300 active:scale-90 active:translate-x-[-4px]"
            onClick={() => navigate('/')}
          >
            ← Back
          </button>
          <div className="text-center">
            <div className="flex items-center justify-center mb-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
              </svg>
              <h1 className="text-3xl font-bold">URL Shortener</h1>
            </div>
            <p className="text-blue-100 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Transform long URLs into powerful, AI-generated short links
            </p>
          </div>
        </div>
      </div>
      
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <form onSubmit={handleSubmit} className="mb-8">
          <div className="mb-4">
            <label htmlFor="url" className="block mb-2 font-medium dark:text-white">
              URL to Shorten:
            </label>
            <input
              id="url"
              type="url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="Enter a long URL"
              className="w-full p-2 border rounded dark:bg-gray-800 dark:border-gray-700 dark:text-white"
              required
            />
          </div>
          
          <div className="flex space-x-3">
            <button 
              type="submit" 
              disabled={isLoading || !url || !!shortenedUrl}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-blue-300"
            >
              {isLoading ? 'Shortening...' : 'Shorten URL'}
            </button>
            
            <button 
              type="button" 
              onClick={handleClear}
              disabled={isLoading || (!url && !shortenedUrl)}
              className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 disabled:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600"
            >
              Clear
            </button>
          </div>
          {error && <p className="text-red-500 mt-2">{error}</p>}
        </form>

        {isLoading ? (
          <div className="p-8 flex flex-col items-center justify-center">
            <LoadingSpinner size="large" />
            <p className="mt-4 text-gray-600 dark:text-gray-300">Shortening your URL...</p>
          </div>
        ) : shortenedUrl && (
          <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded border dark:border-gray-700">
            <h2 className="font-bold mb-2 dark:text-white">Your Shortened URL:</h2>
            <div className="flex flex-col md:flex-row items-center gap-4 mb-4">
              <div className="flex-grow p-3 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md overflow-x-auto dark:text-gray-200">
                {shortenedUrl.shortUrl}
              </div>
              <button
                onClick={copyToClipboard}
                className="bg-gray-200 text-gray-800 dark:bg-gray-600 dark:text-gray-200 py-2 px-4 rounded hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
            </div>
            <div className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              <p>Original URL: <a href={shortenedUrl.originalUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 dark:text-blue-400 hover:underline truncate block max-w-full">{shortenedUrl.originalUrl}</a></p>
            </div>
          </div>
        )}

        {/* Usage Tips */}
        <div className="mt-8 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <h3 className="font-bold text-lg mb-3 text-gray-800 dark:text-white">URL Shortener Tips</h3>
          <ul className="list-disc pl-5 space-y-2 text-gray-600 dark:text-gray-300">
            <li>Use short URLs in social media posts to save character space</li>
            <li>Track click-through rates by using different short URLs for different channels</li>
            <li>Make your links more professional and cleaner in presentations</li>
            <li>Avoid broken links in printed materials by using shorter, easier to type URLs</li>
            <li>Branded short links can increase click-through rates by up to 34%</li>
          </ul>
        </div>
        
        {/* Features */}
        <div className="mt-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
          <h3 className="font-bold text-lg mb-3 text-gray-800 dark:text-white">Features</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <h4 className="font-medium text-blue-700 dark:text-blue-400 mb-2">Quick Shortening</h4>
              <p className="text-gray-600 dark:text-gray-300">Transform any long URL into a short, manageable link in seconds.</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <h4 className="font-medium text-blue-700 dark:text-blue-400 mb-2">Analytics Ready</h4>
              <p className="text-gray-600 dark:text-gray-300">Track clicks and gather insights about your audience.</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg">
              <h4 className="font-medium text-blue-700 dark:text-blue-400 mb-2">Custom Links</h4>
              <p className="text-gray-600 dark:text-gray-300">Create branded short links that reflect your identity.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UrlShortenerPage;
